// composables/useAnalytics.ts
import { useNuxtApp } from '#app';
import { useAuth } from '~/stores/auth';
import {
  AnalyticsEvents,
  type AnalyticsEventName,
  type AnalyticsEventPayload,
  type AnalyticsService,
  type PageMetadata,
  type UtmParams,
  UTM_KEYS,
} from '~/utility/analytics/events';

const fallbackAnalytics: AnalyticsService = {
  track: () => {},
  setUserProfile: () => {},
  isEnabled: () => false,
  isReady: () => false,
  reset: () => {},
  flush: async () => {},
};

type UserAnalyticsPayload = {
  user_id: string | null;
  user_name: string | null;
  user_phone: string | null;
  user_email: string | null;
};

const CHECKOUT_EVENT_NAMES = new Set<string>([
  AnalyticsEvents.CHECKOUT_STARTED,
  AnalyticsEvents.PAYMENT_INITIATED,
  AnalyticsEvents.PAYMENT_FAILED,
  AnalyticsEvents.ORDER_FAILED,
  AnalyticsEvents.PAYMENT_SUCCESS,
  AnalyticsEvents.ORDER_CONFIRMED,
  AnalyticsEvents.ORDER_CANCELLED,

]);

const maskValue = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const str = String(value);
  if (str.includes('@')) {
    const [local, domain] = str.split('@');
    const maskedLocal =
      local.length <= 2 ? '*'.repeat(local.length) : `${local[0]}***${local[local.length - 1]}`;
    return `${maskedLocal}@${domain}`;
  }
  if (/^\d+$/.test(str)) {
    return str.length <= 4 ? '*'.repeat(str.length) : `${'*'.repeat(str.length - 2)}${str.slice(-2)}`;
  }
  if (str.length <= 2) return '*'.repeat(str.length);
  return `${str[0]}***${str[str.length - 1]}`;
};

const EMPTY_USER_DATA: UserAnalyticsPayload = {
  user_id: null,
  user_name: null,
  user_phone: null,
  user_email: null,
};

const UTM_STORAGE_KEY = 'analytics_utm_params';
const UTM_STORAGE_TTL_MS = 5 * 60 * 60 * 1000;
type UtmPayload = UtmParams;

const getAnalyticsService = (): AnalyticsService => {
  const nuxtApp = useNuxtApp();
  return (nuxtApp.$analytics as AnalyticsService | undefined) ?? fallbackAnalytics;
};

const readUtmFromSearch = (): UtmPayload => {
  if (process.server || typeof window === 'undefined') return {};

  try {
    const params = new URLSearchParams(window.location?.search ?? '');
    const utm: UtmPayload = {};

    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) utm[key] = value;
    }

    return utm;
  } catch {
    return {};
  }
};

const loadStoredUtm = (): UtmPayload => {
  if (process.server || typeof window === 'undefined') return {};

  try {
    const raw = window.localStorage?.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { data?: UtmPayload; expires_at?: number };
    const expiresAt = parsed?.expires_at;
    if (!expiresAt || Date.now() > expiresAt) {
      window.localStorage?.removeItem(UTM_STORAGE_KEY);
      return {};
    }
    return parsed?.data ?? {};
  } catch {
    return {};
  }
};

const persistUtm = (utm: UtmPayload) => {
  if (process.server || typeof window === 'undefined') return;
  try {
    const expiresAt = Date.now() + UTM_STORAGE_TTL_MS;
    window.localStorage?.setItem(
      UTM_STORAGE_KEY,
      JSON.stringify({ data: utm, expires_at: expiresAt }),
    );
  } catch {
    // ignore
  }
};

const getUtmParams = (): UtmPayload => {
  if (process.server || typeof window === 'undefined') return {};

  const stored = loadStoredUtm();
  const fromUrl = readUtmFromSearch();
  const merged: UtmPayload = { ...stored, ...fromUrl };

  const hasNewValues =
    Object.keys(fromUrl).length > 0 &&
    UTM_KEYS.some((key) => fromUrl[key] && fromUrl[key] !== stored[key]);

  if (hasNewValues) {
    persistUtm(merged);
  }

  return merged;
};

export const getAnalyticsPageMetadata = (): PageMetadata => {
  if (process.server || typeof window === 'undefined') return {};

  const doc = document.documentElement;
  const scrollDepthPercent = (() => {
    try {
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const viewport = window.innerHeight || doc.clientHeight || 0;
      const fullHeight = Math.max(doc.scrollHeight || 0, viewport);
      if (!fullHeight) return undefined;
      const ratio = (scrollTop + viewport) / fullHeight;
      const percent = Math.min(100, Math.max(0, Math.round(ratio * 100)));
      return Number.isFinite(percent) ? percent : undefined;
    } catch {
      return undefined;
    }
  })();

  const pathname = window.location?.pathname ?? '';
  const search = window.location?.search ?? '';
  const pageUrl = `${pathname}${search}` || undefined;
  const utmParams = getUtmParams();

  return {
    page_name:
      typeof document !== 'undefined' ? (document.title || undefined) : undefined,
    page_url: pageUrl,
    referrer_page:
      typeof document !== 'undefined' && document.referrer
        ? document.referrer
        : undefined,
    scroll_depth_percent: scrollDepthPercent,
    ...utmParams,
  };
};

const getUserAnalyticsPayload = (): UserAnalyticsPayload => {
  if (process.server) return EMPTY_USER_DATA;

  try {
    const auth = useAuth();
    const isAuthenticated = Boolean(auth?.access_token && auth.user);

    if (!isAuthenticated) return EMPTY_USER_DATA;

    return {
      user_id: auth.user?.id ? String(auth.user.id) : null,
      user_name: maskValue(auth.user?.first_name) || null,
      user_phone: maskValue(auth.user?.phone) || null,
      user_email: maskValue(auth.user?.email) || null,
    };
  } catch {
    return EMPTY_USER_DATA;
  }
};

const sanitizePayload = <EventName extends AnalyticsEventName>(
  event: EventName,
  payload?: AnalyticsEventPayload<EventName> | null,
): Record<string, any> | null => {
  // Allow empty payload (useful for pure page events)
  if (payload == null) return {};

  if (typeof payload !== 'object') {
    if (import.meta.dev) {
      console.warn(
        `[Analytics] Ignoring payload for event "${event}" because it must be an object.`,
      );
    }
    return null;
  }

  // Clone so callers can't mutate what we send
  return { ...(payload as Record<string, any>) };
};

const mirrorEventToBackend = async (
  eventName: string,
  finalPayload: Record<string, any>,
) => {
  if (process.server) return;

  try {
    await useAPIHandler('post', '/insertEvent', {
      auth: true,
      body: { data: { event_name: eventName, ...finalPayload } },
    });
  } catch (error) {
    if (import.meta.dev) {
      console.warn('[Analytics] Failed to mirror event to backend', error);
    }
  }
};

export function useAnalytics(): AnalyticsService;
export function useAnalytics<EventName extends AnalyticsEventName>(
  event: EventName,
  payload?: AnalyticsEventPayload<EventName> | null,
): void;
export function useAnalytics<EventName extends AnalyticsEventName>(
  event?: EventName,
  payload?: AnalyticsEventPayload<EventName> | null,
): AnalyticsService | void {
  const analytics = getAnalyticsService();

  // Getter mode: const analytics = useAnalytics();
  if (!event) {
    return analytics;
  }

  const name = typeof event === 'string' ? event.trim() : '';

  if (!name) {
    if (import.meta.dev) {
      console.warn('[Analytics] Event name is required to track analytics events.');
    }
    return;
  }

  const basePayload = sanitizePayload(name as EventName, payload);
  if (basePayload === null) {
    return;
  }

  const userPayload = getUserAnalyticsPayload();
  const pageMetadata = getAnalyticsPageMetadata();

  const finalPayload = {
    ...pageMetadata,
    ...basePayload,
    ...userPayload,
  } as AnalyticsEventPayload<EventName>;

  analytics.track(name as EventName, finalPayload);
  if (CHECKOUT_EVENT_NAMES.has(name)) {
    void mirrorEventToBackend(name, finalPayload);
  }
}

export const useAnalyticsEvents = () => AnalyticsEvents;
